import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Download, ArrowRight, Loader } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { httpsCallable } from "firebase/functions";
import { functions } from "../config/firebase";
import { Ticket } from "../types";

interface ConfirmPaymentResponse {
  success: boolean;
  alreadyProcessed: boolean;
  orderId: string;
  tickets: Ticket[];
}

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");
  const { currentUser } = useAuth();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [error, setError] = useState("");
  const hasConfirmed = useRef(false);

  useEffect(() => {
    if (currentUser && !hasConfirmed.current) {
      if (sessionId) {
        hasConfirmed.current = true;
        confirmAndFetchTickets();
      } else if (orderId) {
        hasConfirmed.current = true;
        fetchOrderTickets(orderId);
      }
    }
  }, [currentUser, sessionId, orderId]);

  const confirmAndFetchTickets = async () => {
    if (!currentUser || !sessionId) return;

    setLoading(true);
    setError("");

    try {
      // First, try to confirm the payment directly with Stripe
      const confirmPayment = httpsCallable<
        { sessionId: string },
        ConfirmPaymentResponse
      >(functions, "confirmPayment");

      const result = await confirmPayment({ sessionId });
      const data = result.data;

      if (data.success && data.tickets.length > 0) {
        setTickets(data.tickets);

        // Auto-download PDFs for new tickets
        if (!data.alreadyProcessed) {
          // Small delay to ensure UI is updated first
          setTimeout(() => {
            data.tickets.forEach((ticket, index) => {
              if (ticket.pdfUrl) {
                // Stagger downloads to avoid browser blocking
                setTimeout(() => {
                  downloadPDF(
                    ticket.pdfUrl,
                    `biglietto-${ticket.ticketCode}.pdf`,
                  );
                }, index * 500);
              }
            });
          }, 1000);
        }
      } else {
        // Fallback: try to fetch existing tickets
        await fetchTickets();
      }
    } catch (err: any) {
      console.error("Error confirming payment:", err);
      // Fallback: try to fetch existing tickets
      await fetchTickets();
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = (url: string, filename: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchTickets = async () => {
    if (!currentUser) return;

    try {
      const getUserTickets = httpsCallable(functions, "getUserTickets");
      const result = await getUserTickets({ uid: currentUser.uid });
      const data = result.data as { tickets: Ticket[] };
      setTickets(data.tickets || []);
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError(t.paymentSuccess.error);
    }
  };

  const fetchOrderTickets = async (orderIdParam: string) => {
    if (!currentUser) return;

    setLoading(true);
    setError("");

    try {
      const getUserTickets = httpsCallable(functions, "getUserTickets");
      const result = await getUserTickets({ uid: currentUser.uid });
      const data = result.data as { tickets: Ticket[] };
      const allTickets = data.tickets || [];

      // Filter to only tickets from this specific order
      const orderTickets = allTickets.filter(
        (ticket) => ticket.orderId === orderIdParam,
      );
      setTickets(orderTickets);

      // Auto-download PDFs
      if (orderTickets.length > 0) {
        setTimeout(() => {
          orderTickets.forEach((ticket, index) => {
            if (ticket.pdfUrl) {
              setTimeout(() => {
                downloadPDF(
                  ticket.pdfUrl,
                  `biglietto-${ticket.ticketCode}.pdf`,
                );
              }, index * 500);
            }
          });
        }, 1000);
      }
    } catch (err) {
      console.error("Error fetching order tickets:", err);
      setError(t.paymentSuccess.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gray-50">
      <div className="container-custom">
        <div className="max-w-2xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-12">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-scale-in">
              <CheckCircle className="w-14 h-14 text-green-500" />
            </div>
            <h1 className="font-display text-4xl font-bold text-green-600 mb-4">
              {t.paymentSuccess.title}
            </h1>
            <p className="text-gray-600 text-lg">
              {t.paymentSuccess.description}
            </p>
          </div>

          {/* Tickets */}
          <div className="card p-8 mb-8">
            <h2 className="font-semibold text-xl text-gray-900 mb-6">
              {t.paymentSuccess.yourTickets}
            </h2>

            {loading ? (
              <div className="text-center py-12">
                <Loader className="w-12 h-12 text-romanian-blue mx-auto animate-spin mb-4" />
                <p className="text-gray-600">{t.paymentSuccess.loading}</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={() => {
                    hasConfirmed.current = false;
                    if (orderId) {
                      fetchOrderTickets(orderId);
                    } else {
                      confirmAndFetchTickets();
                    }
                  }}
                  className="btn-outline"
                >
                  {t.paymentSuccess.retry}
                </button>
              </div>
            ) : tickets.length > 0 ? (
              <div className="space-y-4">
                {tickets.slice(0, 5).map((ticket) => (
                  <div
                    key={ticket.id}
                    className="border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {ticket.participant?.nome} {ticket.participant?.cognome}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t.tickets.code}: {ticket.ticketCode}
                      </p>
                    </div>
                    <a
                      href={ticket.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary flex items-center space-x-2"
                    >
                      <Download className="w-4 h-4" />
                      <span>{t.tickets.downloadPdf}</span>
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">
                  {t.paymentSuccess.emptyState}
                </p>
                <button
                  onClick={() => {
                    hasConfirmed.current = false;
                    if (orderId) {
                      fetchOrderTickets(orderId);
                    } else {
                      confirmAndFetchTickets();
                    }
                  }}
                  className="btn-outline"
                >
                  {t.paymentSuccess.refresh}
                </button>
              </div>
            )}
          </div>

          {/* Next Steps */}
          <div className="card p-8 bg-primary-50 border border-primary-100">
            <h3 className="font-semibold text-lg text-romanian-blue mb-4">
              {t.paymentSuccess.nextSteps}
            </h3>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-romanian-blue text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">
                  1
                </span>
                <span>{t.paymentSuccess.step1}</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-romanian-blue text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">
                  2
                </span>
                <span>{t.paymentSuccess.step2}</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="w-6 h-6 bg-romanian-blue text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">
                  3
                </span>
                <span>{t.paymentSuccess.step3}</span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="text-center mt-8">
            <Link
              to="/"
              className="btn-outline inline-flex items-center space-x-2"
            >
              <span>{t.paymentSuccess.backHome}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
